import React, { useEffect, useState } from "react";
import { htmlPrint } from "./htmlPrinter";
import { xmlPrint } from "./xmlPrinter";
import HCPrint from "./hardWareControllerPrinter";
import { kotPrinter } from "./kotPrinter";
import { v4 as uuidv4 } from "uuid";
import CWHCPrint from "./CWHardwareController";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useRestaurantStore from "../../stores/useRestaurantStore";
import { consolidateBillItems } from "./consolidateBillItems";

const PrintController = (receiptDataIn, cart) => {
  const tillData = useAuthStore.getState().tillData;
  // Repeat-fired-line consolidation (TSK-2930) only applies to Restaurant (tillLayout 2), where
  // the same product can be fired to the kitchen across multiple batches and needs to collapse
  // into one bill row. Retail/SuperMarket (tillLayout 1) never fires in batches, so its bill
  // must print exactly the line items the order was built with.
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const tillLayout = parseInt(tillaccess?.layout === null || undefined ? 1 : tillaccess?.layout);
  const isRestaurant = tillLayout === 2;

  // Build a local copy for printing so callers' cartToDb/finalCartObj objects (which may still
  // be read afterward, e.g. useOrderProcessing.js's post-print error-recovery logging) are never
  // mutated by the canceled-item filter or bill-line consolidation below.
  const activeItems = (receiptDataIn.items || []).filter((item) => item.isCanceled !== "Y");
  const receiptData = {
    ...receiptDataIn,
    items: isRestaurant ? consolidateBillItems(activeItems) : activeItems,
  };
  // Unguarded reads here used to throw synchronously on a till record with a null
  // printTemplate/hardwareController relation, which -- since this function is called
  // unawaited from inside salesHistoryUtils.js's try block, ahead of the KOT print call --
  // silently killed BOTH invoice and KOT printing for that order with only a console.error.
  // printTemplate/hardwareController are optional relations on cwrTill elsewhere in the
  // codebase (e.g. printInterceptor.js, tillCloseHCPrinter.js already guard them), so treat
  // them the same way here.
  const ObFlag = tillData?.tillAccess?.cwrTill?.printTemplate?.obController === "Y" ? true : false;
  const CWHardwareController = tillData?.tillAccess?.cwrTill?.printTemplate?.cwController === "Y" ? true : false;
  const isPrintModeXML = tillData?.tillAccess?.cwrTill?.hardwareController?.printReceipt === "Y" ? true : false;
  const defaultCustomer = tillData?.tillAccess?.csBunit?.b2cCustomer;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const dineInValue = useRestaurantStore.getState().dineIn;
  console.debug("[PrintController] entry", { sOrderID: receiptData?.sOrderID, dineInValue, hasCart: !!cart, ObFlag, CWHardwareController, isPrintModeXML });

  // Consolidated print routing (was duplicated identically between the dine-in and
  // non-dine-in branches below).
  // CW Browser guard: htmlPrint's window.print() dialog isn't usable inside the
  // Electron/CW Browser shell, so when no explicit controller flag picked a printer
  // and XML mode is off, route through the same native hardware-controller bridge
  // used when cwController is explicitly "Y" instead of silently falling through
  // to a print dialog that can't render there. F-04: only take this path when a
  // hardware controller is actually configured for the till (imageUrl present) --
  // CWHardwareController.js reads that URL unguarded, so an unconfigured till would
  // otherwise silently POST an empty template to "undefinedprinter" (no error shown,
  // nothing prints). Those tills printed fine via htmlPrint before this branch
  // existed, so fall back to it when there's no controller to route to.
  const printerConfigured = !!tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  const doPrint = (data) => {
    if (ObFlag) {
      HCPrint(data);
    } else if (CWHardwareController) {
      CWHCPrint(data);
    } else if (isPrintModeXML) {
      xmlPrint(data, "Receipt");
    } else if (window.electronAPI && printerConfigured) {
      CWHCPrint(data);
    } else {
      htmlPrint(data, "Receipt");
    }
  };

  if (dineInValue === "N") {
    console.debug("[PrintController] dineIn === 'N' branch, about to read cart.items", { hasCart: !!cart, cartIsUndefined: cart === undefined });
    const printKOTArray = cart.items.reduce((result, current) => {
      const category = current.productionCenter;
      if (!result[category]) {
        result[category] = [];
      }
      result[category].push(current);
      return result;
    }, {});
    if (posConfig?.enableKOTPrint === "Y" && !CWHardwareController) {
      kotPrinter(printKOTArray, cart, "receipt");
    }
    setTimeout(() => {
      doPrint(receiptData);
    }, 1000);
  } else {
    doPrint(receiptData);
  }
};
export default PrintController;
